import {
  availableSoon,
  cashback,
  home,
  investments,
  loan,
  pix,
  products,
  receive,
  transfer,
} from '@portal/config/app.json';
import RolesType from '@portal/enum/rolesType';
import useReduxState from '@portal/hooks/useReduxState';
import React, { useState } from 'react';

import CheckIcon from '@portal/assets/svg/home-buttons/ic_approve.svg?react';
import CashbackIcon from '@portal/assets/svg/home-buttons/ic_coin.svg?react';
import MobileIcon from '@portal/assets/svg/ic_mobile.svg?react';
import ReceiveIcon from '@portal/assets/svg/ic_new_received.svg?react';
import TransferIcon from '@portal/assets/svg/ic_new_transfer.svg?react';
import LeafIcon from '@portal/assets/svg/leaf_icon.svg?react';

import AccountSplitIcon from '@portal/assets/svg/group_user.svg?react';
import LoanIcon from '@portal/assets/svg/home-buttons/ic_loan.svg?react';
import PayIcon from '@portal/assets/svg/ic_new_pay.svg?react';
import PixIcon from '@portal/assets/svg/ic_pix_white.svg?react';
import AccessListControl from '@portal/components/AccessListControl/AccessListControl';
import { getRouteStackPath } from '@portal/config/routes';
import { EventType } from '@portal/enum/analytics';
import { AccountTaxIdentifierType } from '@portal/models/enumerators';
import { sendEvent } from '@portal/services/firebase';
import { translate } from '@portal/services/i18n';
import { hasAccess } from '@portal/utils/hasAccess';
import './AdvancedHomeButtons.less';

import SimpleModal from '@portal/components/SimpleModal/SimpleModal';
import { useNavigation } from '@portal/hooks/useNavigation';
import { PaymentAccountType } from '@portal/models/enumerators/paymentAccount';
import { selectRechargeEnabled } from '@portal/utils/accountSettings';

interface AdvancedHomeButtonsProps {
  hasInvestments: boolean;
  testId: string;
}

const AdvancedHomeButtons = ({
  hasInvestments,
  testId,
}: AdvancedHomeButtonsProps) => {
  const navigate = useNavigation();

  const {
    user: { roles, me },
    settings: { loanSettings },
    theme: { currentTheme },
    account: { config },
  } = useReduxState();

  const [hoveredIndex, setHoveredIndex] = React.useState<string | null>(null);
  const [isComingSoonModalVisible, setIsComingSoonModalVisible] =
    useState(false);
  const showInvestmentsCondition = investments.onlyForExistingInvestments
    ? investments.enabled && hasInvestments
    : investments.enabled;
  const isRechargeEnable = selectRechargeEnabled(config);

  const handleAccessPix = () => {
    if (typeof me?.canAccessPix !== 'undefined' && !me?.canAccessPix) {
      return false;
    }
    return true;
  };

  const hasAccessAccountSplit = () => {
    const isLegalPerson =
      me.account.type === AccountTaxIdentifierType.CORPORATE;

    if (!isLegalPerson) return false;

    const hasSecondaryAccount = me.account.paymentAccounts?.some(
      (acc) => acc.type === PaymentAccountType.ACCOUNTSECONDARY
    );

    return Boolean(hasSecondaryAccount && products.enableAccountSplit);
  };

  const handleNavigateToRecharge = () => {
    if (!isRechargeEnable) {
      setIsComingSoonModalVisible(true);
      return;
    }

    navigate(getRouteStackPath('RECHARGE', 'RECHARGE_SELECTION_PAGE'));
    sendEvent(EventType.BOTTOM_MODAL_SERVICES_RECHARGE);
  };

  const actions = () => {
    const quickActions = [
      {
        visible: handleAccessPix() && (pix.enabled || availableSoon.pix),
        text: translate('PAGES.HOME.CARD.PIX'),
        hasAccess: [RolesType.PIX_MANAGE_KEYS, RolesType.TRANSACTION_CREATE],
        icon: <PixIcon />,
        link: hasAccess(
          roles,
          RolesType.TRANSACTION_CREATE || RolesType.PIX_MANAGE_KEYS
        )
          ? getRouteStackPath('PIX', 'PIX')
          : '',
        onClick: () => sendEvent(EventType.HOME_PIX_BUTTON),
      },
      {
        visible: true,
        text: translate('PAGES.HOME.CARD.TRANSFER'),
        hasAccess: [RolesType.TRANSACTION_CREATE, RolesType.FAVORED_CREATE],
        icon: <TransferIcon />,
        link: hasAccess(roles, RolesType.TRANSACTION_CREATE)
          ? getRouteStackPath('TRANSFERS', 'TRANSFERS')
          : '',
        onClick: () => sendEvent(EventType.HOME_TRANSFER_BUTTON),
      },
      {
        visible: receive.enabled,
        text: translate('PAGES.HOME.CARD.RECEIVE'),
        hasAccess: [RolesType.TRANSACTION_CREATE],
        icon: <ReceiveIcon />,
        link: hasAccess(roles, RolesType.TRANSACTION_CREATE)
          ? getRouteStackPath('HOME', 'RECEBER')
          : '',
        onClick: () => sendEvent(EventType.HOME_RECEIVABLE_BUTTON),
      },
      {
        visible: home.allowPay,
        text: translate('PAGES.HOME.CARD.PAY'),
        hasAccess: [RolesType.TRANSACTION_CREATE],
        icon: <PayIcon />,
        link: hasAccess(roles, RolesType.TRANSACTION_CREATE)
          ? getRouteStackPath('HOME', 'PAY')
          : '',
        onClick: () => sendEvent(EventType.HOME_PAYABLE_BUTTON),
      },
      {
        visible:
          me.account.type === AccountTaxIdentifierType.CORPORATE &&
          transfer.twoFactorApprovals,
        text: translate('PAGES.HOME.CARD.APPROVE'),
        hasAccess: [RolesType.TRANSACTION_CREATE],
        icon: <CheckIcon />,
        link: hasAccess(roles, RolesType.TRANSACTION_READ)
          ? getRouteStackPath('APPROVED', 'APPROVED_PAGE')
          : '',
        onClick: () => sendEvent(EventType.HOME_CASHBACK_BUTTON),
      },
      {
        visible: home.portal,
        icon: <LeafIcon />,
        hasAccess: [],
        text: translate('PAGES.HOME.CARD.PORTAL'),
        link: getRouteStackPath('LOAN', 'LOAN_PAGE'),
        onClick: () => sendEvent(EventType.HOME_LOAN_BUTTON),
      },
      {
        visible:
          loan.enabled && (loanSettings?.online || loan.enableAvailableSoon),
        icon: <LoanIcon />,
        hasAccess: [],
        text: translate('PAGES.HOME.CARD.LOAN'),
        link: getRouteStackPath('LOAN', 'LOAN_PAGE'),
        onClick: () => sendEvent(EventType.HOME_LOAN_BUTTON),
      },
      {
        visible: cashback.enabled || cashback.money,
        hasAccess: [],
        icon: <CashbackIcon />,
        text: translate('PAGES.HOME.CARD.CASHBACK'),
        link: hasAccess(roles, RolesType.TRANSACTION_READ)
          ? getRouteStackPath('CASHBACK', 'CASHBACK_PAGE')
          : '',
        onClick: () => sendEvent(EventType.HOME_CASHBACK_BUTTON),
      },
      {
        visible: home.agroAccess,
        icon: <CashbackIcon />,
        hasAccess: [],
        text: translate('PAGES.HOME.CARD.AGRO'),
        link: getRouteStackPath('LOAN', 'LOAN_PAGE'),
        onClick: () => sendEvent(EventType.HOME_LOAN_BUTTON),
      },
      {
        visible: showInvestmentsCondition,
        icon: <LoanIcon />,
        hasAccess: [],
        link: getRouteStackPath('INVESTMENTS', 'INVESTMENTS_PAGE'),
        text: translate('PAGES.HOME.CARD.INVEST'),
        onClick: () => sendEvent(EventType.HOME_LOAN_BUTTON),
      },
      {
        visible: hasAccessAccountSplit(),
        text: translate('PAGES.ACCOUNT_SPLIT.TITLE'),
        hasAccess: [RolesType.MY_ACCOUNT],
        icon: <AccountSplitIcon />,
        link: hasAccess(roles, RolesType.MY_ACCOUNT)
          ? getRouteStackPath('ACCOUNT_SPLIT', 'ACCOUNT_SPLIT_PAGE')
          : '',
        onClick: () => sendEvent(EventType.HOME_BUTTON),
      },
      {
        visible: home.enableRecharge,
        icon: <MobileIcon />,
        hasAccess: [RolesType.ACCOUNT_READ],
        text: translate('PAGES.HOME.CARD.RECHARGE'),
        onClick: () => handleNavigateToRecharge(),
      },
    ];

    return quickActions
      .filter((action) => action.visible)
      .map((item) => (
        <AccessListControl key={item.text} accessRole={item.hasAccess}>
          <div
            className="advanced-home-buttons__card"
            onClick={() => {
              if (item.link) {
                navigate(item.link);
              }

              if (item.onClick) {
                item.onClick();
              }
            }}
            onMouseEnter={() => setHoveredIndex(item.text)}
            onMouseLeave={() => setHoveredIndex(null)}
            style={{
              border:
                hoveredIndex === item.text
                  ? `2px solid ${currentTheme.primaryColor}`
                  : '2px solid transparent',
              background:
                hoveredIndex === item.text
                  ? `${currentTheme.primaryColor}14`
                  : undefined,
            }}
          >
            <div
              className="advanced-home-buttons__icon"
              style={{ background: currentTheme.background }}
            >
              {item.icon}
            </div>
            <div className="advanced-home-buttons__title">{item.text}</div>
          </div>
        </AccessListControl>
      ));
  };

  return (
    <>
      <div className="advanced-home-buttons" data-testid={testId}>
        {actions()}
      </div>
      <SimpleModal
        visible={isComingSoonModalVisible}
        setVisible={setIsComingSoonModalVisible}
        onContinue={() => {
          setIsComingSoonModalVisible(false);
        }}
        description={translate('PAGES.HOME.COMING_SOON.SECOND_DESCRIPTION')}
        title={translate('PAGES.HOME.COMING_SOON.TITLE')}
        buttonText={translate('SHARED.CLOSE')}
      />
    </>
  );
};

export default AdvancedHomeButtons;
